import type { JSONContent } from '@tiptap/core';
import { z } from 'zod';

import { textLength } from '@/components/form/richText';

import type { PageForm, PageLimits } from './pageLabels';

/*
 * The editor's field rules (design.md "The editor page", P13), so the form
 * refuses what the server would before sending it. Every bound is the
 * reply's `limits`, which the server reads from its column sizes and
 * settings; the module holds none of its own (I5, P3).
 */

const atMost = (max: number) => `Use ${max.toLocaleString('en-US')} characters or fewer`;

const TITLE_MISSING = 'Enter a title';
const SUMMARY_MISSING = 'Enter a summary to publish';
const CONTENT_MISSING = 'Add content to publish';

/** Title, summary, header image and links: what Save Draft checks. */
export function pageSchema(limits: PageLimits) {
  return z.object({
    title: z
      .string()
      .trim()
      .min(1, TITLE_MISSING)
      .max(limits.titleMaxLength, atMost(limits.titleMaxLength)),
    summary: z
      .string()
      .trim()
      .max(limits.summaryMaxLength, atMost(limits.summaryMaxLength)),
    headerImage: z
      .object({
        key: z.string(),
        url: z.string().nullable(),
        alt: z.string().trim().max(limits.altMaxLength, atMost(limits.altMaxLength)),
      })
      .nullable(),
    links: z
      .array(
        z.object({
          resourceType: z.enum(['DOCUMENT', 'CAMPAIGN', 'PETITION']),
          resourceId: z.string(),
          title: z.string().nullable(),
          available: z.boolean(),
        }),
      )
      .refine(
        (links) =>
          links.filter((link) => link.resourceType === 'DOCUMENT').length <=
          limits.maxLinkedDocuments,
        `Link up to ${limits.maxLinkedDocuments} documents`,
      ),
  });
}

/**
 * True when a document has something to show once rendered: visible text,
 * an image or a call-to-action button. An empty paragraph, an empty heading
 * and text of spaces only do not count, the rule the backend holds on
 * publish (backend D44, P42).
 */
export function hasContent(node: JSONContent | null | undefined): boolean {
  if (!node) return false;
  if (node.type === 'image' || node.type === 'ctaButton') return true;
  if (typeof node.text === 'string' && node.text.trim() !== '') return true;
  return (node.content ?? []).some(hasContent);
}

/** The content's length against the reply's limit, or null when it fits. */
export function contentRefusal(
  content: JSONContent | null,
  limits: PageLimits,
): string | null {
  return textLength(content) > limits.richTextMaxLength
    ? atMost(limits.richTextMaxLength)
    : null;
}

export type RefusedField = 'title' | 'summary' | 'content';

export interface Refusal {
  field: RefusedField;
  message: string;
}

/**
 * What Publish Page, and Save Changes on a published page, would be refused
 * for, in the form's order so focus can go to the first (P13, OQ1 default:
 * title, summary and content). The bounds are checked too, so a publish
 * never sends what a save would refuse.
 */
export function publishRefusals(
  form: PageForm,
  content: JSONContent | null,
  limits: PageLimits,
): Refusal[] {
  const refusals: Refusal[] = [];
  const title = form.title.trim();
  const summary = form.summary.trim();

  if (!title) refusals.push({ field: 'title', message: TITLE_MISSING });
  else if (title.length > limits.titleMaxLength) {
    refusals.push({ field: 'title', message: atMost(limits.titleMaxLength) });
  }

  if (!summary) refusals.push({ field: 'summary', message: SUMMARY_MISSING });
  else if (summary.length > limits.summaryMaxLength) {
    refusals.push({ field: 'summary', message: atMost(limits.summaryMaxLength) });
  }

  const tooLong = contentRefusal(content, limits);
  if (!hasContent(content)) refusals.push({ field: 'content', message: CONTENT_MISSING });
  else if (tooLong) refusals.push({ field: 'content', message: tooLong });

  return refusals;
}
